import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function Onboarding() {
  const navigate = useNavigate();
  const { user, setUser, accessConfig } = useAuth();
  const [mode, setMode] = useState("institution");
  const [code, setCode] = useState("");
  const [joining, setJoining] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!code.trim()) {
      toast.error("Enter your class or institution code to begin.");
      return;
    }
    setJoining(true);
    try {
      const endpoint = mode === "class" ? "/classes/join" : "/institutions/join";
      const { data } = await api.post(endpoint, { join_code: code });
      if (data.user) setUser(data.user);
      toast.success(mode === "class" ? "Class access linked." : "Institution access linked.");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not apply that code.");
    } finally {
      setJoining(false);
    }
  };

  useEffect(() => {
    if (user?.access_granted) navigate("/dashboard");
  }, [user, navigate]);

  if (user?.access_granted) return null;

  return (
    <div className="min-h-screen bg-forge-900 text-forge-100">
      <Header minimal />
      <main className="max-w-3xl mx-auto px-6 lg:px-10 py-16 sm:py-24">
        <div className="text-[10px] tracking-[0.4em] uppercase text-ember-500 mb-3">Pilot Access</div>
        <h1 className="font-serif text-4xl sm:text-5xl tracking-tight mb-3">Enter your class or institution code to begin.</h1>
        <p className="text-sm text-forge-100/60 mb-10 max-w-2xl">
          ThoughtSmith™ pilot access is linked to a class, library, or institution so learning activity is attributed correctly.
        </p>

        <form onSubmit={submit} className="border border-white/10 bg-forge-800/35 rounded-sm p-6 sm:p-8 space-y-7">
          <div className="grid grid-cols-2 gap-2">
            {[
              ["institution", "Institution"],
              ["class", "Class"],
            ].map(([value, label]) => (
              <button
                key={value}
                type="button"
                onClick={() => setMode(value)}
                className={`px-4 py-3 border rounded-sm text-xs uppercase tracking-[0.22em] transition-colors ${mode === value ? "border-ember-500 text-ember-400 bg-ember-500/8" : "border-white/10 text-forge-100/55 hover:border-ember-500/50"}`}
              >
                {label}
              </button>
            ))}
          </div>

          <label className="block">
            <span className="block text-[10px] uppercase tracking-[0.3em] text-forge-100/50 mb-3">
              {mode === "class" ? "Class Code" : "Library / Institution Code"}
            </span>
            <input
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              className="w-full bg-transparent border-b border-white/15 focus:border-ember-500 outline-none py-3 text-forge-100 placeholder-forge-100/30 text-3xl font-mono tracking-[0.16em]"
              placeholder={mode === "class" ? "SMITH8" : "FLINTLIBRARY"}
              autoComplete="off"
            />
          </label>

          <button disabled={joining} className="px-6 py-2.5 bg-ember-500 hover:bg-ember-400 disabled:opacity-60 text-forge-900 font-semibold rounded-sm tracking-wide transition-all">
            {joining ? "Applying..." : "Continue"}
          </button>
        </form>

        {accessConfig.allow_independent_signups && (
          <button
            onClick={() => navigate("/dashboard")}
            className="mt-8 text-xs uppercase tracking-[0.25em] text-forge-100/45 hover:text-ember-400 transition-colors"
          >
            Continue as Independent Learner
          </button>
        )}
      </main>
    </div>
  );
}
